import { Button } from '@/components/ui/button';
import { Tag, Trash2, CircleDot, X } from 'lucide-react';

interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onAddTags?: () => void;
  onRemoveTags?: () => void;
  onSetStatus?: () => void;
  onUpdateMetadata?: () => void;
  onDelete?: () => void;
  entityType: 'clip' | 'vfx_shot';
}

/**
 * Sticky toolbar that appears when items are selected
 * Provides bulk actions for managing multiple items at once
 */
export function BulkActionBar({
  selectedCount,
  onClearSelection,
  onAddTags,
  onSetStatus,
  onDelete,
  entityType
}: BulkActionBarProps) {
  if (selectedCount === 0) return null;

  const entityLabel = entityType === 'clip' ? 'clip' : 'shot';
  const entityLabelPlural = entityType === 'clip' ? 'clips' : 'shots';

  return (
    <div className="sticky top-0 z-10 bg-blue-50 dark:bg-blue-950/30 border-b border-blue-200 dark:border-blue-800 px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="font-medium text-blue-900 dark:text-blue-100">
            {selectedCount} {selectedCount === 1 ? entityLabel : entityLabelPlural} selected
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 hover:bg-blue-100 dark:hover:bg-blue-900/50"
          >
            <X className="h-4 w-4 mr-2" />
            Clear selection
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {onAddTags && (
            <Button
              variant="outline"
              size="sm"
              onClick={onAddTags}
              className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <Tag className="h-4 w-4 mr-2" />
              Edit Tags
            </Button>
          )}
          {onSetStatus && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSetStatus}
              className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <CircleDot className="h-4 w-4 mr-2" />
              Set Status
            </Button>
          )}
          {onDelete && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
